<template>
  <div class="wrapper" :style="{ backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : '' }">
    <div class="content-wrapper step-2">
      <div class="step-wrapper">
        <div class="step">{{ $t("app.campaign.single.editStepTitle") }}</div>
        <div class="form-wrapper">
          <p class="explanations">
            {{ $t("app.campaign.single.editCardExplanation") }} <br />
            {{ $t("app.campaign.single.editCardPersonalize") }}
          </p>
          <div v-if="state?.publicCampaignData?.campaign" class="campaign-layout">
            <CampaignCard data-step="19" v-model="state.publicCampaignData" @changeImage="prepareUploadHeaderImage" @changePublicName="(e) => (state.publicCampaignData.campaign.headline = e)" @changeJobTitle="(e) => (state.publicCampaignData.campaign.job_title = e)" @changeJobDescription="(e) => (state.publicCampaignData.campaign.tagline = e)" @toggleError="(val) => (state.publicCampaignCardError = val)" :mode="campaignCardMode" :text_colour="state.publicCampaignData?.branding?.text_color" :elem_colour="state.publicCampaignData?.branding?.highlight_color" />
            <CampaignThemeCard ref="campaignThemeRef" @toggleTheme="toggleTooltip" @changeTextColour="(e) => (state.publicCampaignData.branding.text_color = e)" @changeThemeColour="(e) => (state.publicCampaignData.branding.highlight_color = e)" @setHeaderImage="handleHeaderImage" @setBackground="handleBGSelect" @newBackground="handleBGUploaded" />
          </div>
          <div class="buttons-inline">
            <Button class="secondary icon icon-left no-dark-mode" :label="$t('app.campaign.single.backToCampaignSettings')" icon="bi-chevron-left" @click="goBack()" />
            <Button
              class="icon icon-left no-dark-mode green"
              :disabled="!canGoNext"
              :loading="state.isLoading"
              :label="$t('app.campaign.single.saveChanges')"
              icon="bi-check-lg"
              @click="updateCampaign()"
              v-tooltip="{
                content: $t('app.campaign.single.fieldsMissing'),
                triggers: canGoNext ? [] : ['hover'],
              }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { useToast } from "vue-toastification";
import { cloneDeep } from "lodash";

import { useAppStore } from "@/stores/app.js";
import { useImageStore } from "@/stores/image.js";

import { getImage, getFileFromURL } from "@/composables/getImage.js";
import { useCampaign, useEditCampaign } from "@/queries/campaigns.query";
import { Campaign } from "@/types/campaign";

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const router = useRouter();

const appStore = useAppStore();
const { uploadImage } = useImageStore();

const { data: campaign, isPending: isCampaignPending } = useCampaign();
const { mutateAsync: editCampaign } = useEditCampaign();

const emits = defineEmits(["handleBGUpdate"]);

const campaignCardMode = ref("new");
const campaignThemeRef = ref(null); //TODO FIX when Campaign theme has not been opened
const bgImageUrl = ref(null);

const state = reactive({
  publicCampaignData: cloneDeep(campaign.value) as Campaign,
  isLoading: false,
  campaign_image: null,
  campaign_image_deleted: false,
  campaign_bg_image: null,
  campaign_bg_image_deleted: false,
  publicCampaignCardError: false,
  mode: "new",
});

const canGoNext = computed(() => {
  if (state.publicCampaignCardError) return false;

  return state.publicCampaignData?.campaign?.headline && state.publicCampaignData?.campaign?.job_title && state.publicCampaignData?.campaign?.tagline;
});

function goBack() {
  appStore.showCampaignLinkInfo = true;
  appStore.campaignParametersTab = 3; //preview tab
  router.push({
    name: "campaign",
    params: {
      spaceId: route.params.spaceId,
      campaignId: route.params.campaignId,
    },
    query: {
      tab: 'Parameters',
    }
  });
}

async function updateCampaign() {
  state.isLoading = true;
  if (state.publicCampaignCardError) {
    return toast.error(t("app.campaign.single.invalidPublicInfo"));
  }

  // if (state.informationsError) {
  //   return toast.error(t("app.campaign.single.invalidPrivateName"));
  // }

  if (state.campaign_image) {
    const { image_id } = await uploadImage(route.params.companyId, "campaign_public_image", state.campaign_image);

    if (image_id) {
      state.publicCampaignData.branding.banner_image_id = image_id;
    } else {
      toast.error(t("app.campaign.single.imageUploadError"));
    }
  } else if (state.campaign_image_deleted) {
    //There was an image now there is none
    state.publicCampaignData.branding.banner_image_id = null;
  }

  if (state.campaign_bg_image) {
    const { image_id } = await uploadImage(route.params.companyId, "campaign_public_bg_image", state.campaign_bg_image);

    if (image_id) {
      state.publicCampaignData.branding.background_image_id = image_id;
    } else {
      toast.error(t("app.campaign.single.imageUploadError"));
    }
  } else if (state.campaign_bg_image_deleted) {
    //There was an image now there is none
    state.publicCampaignData.branding.background_image_id = null;
  }

  // Apply campaign information and branding changes
  await editCampaign({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.companyId as string,
      campaignId: state.publicCampaignData.id,
    },
    campaign: {
      id: state.publicCampaignData.id,
      campaign_version: state.publicCampaignData.campaign_version,
      branding: state.publicCampaignData.branding,
      campaign: state.publicCampaignData.campaign,
      assessment: state.publicCampaignData.assessment,
    }
  }, {
    onSuccess: (data) => {
      if (data?.id) {
        toast.success(t("app.campaign.single.saved"));
      } else {
        toast.error(t("app.campaign.single.updateError"));
      }
    },
    onError: (error) => {
      toast.error(t("app.campaign.single.updateError"));
    }
  });

  //Go back to campaign page with closed setting
  appStore.showCampaignLinkInfo = false;
  router.push({
    name: "campaign",
    params: {
      spaceId: route.params.spaceId,
      campaignId: route.params.campaignId,
    },
  });

  state.isLoading = false;
}

function toggleTooltip(value) {
  if (value) {
    campaignCardMode.value = "new";
  } else {
    campaignCardMode.value = "hide";
  }
}

async function handleBGSelect(data) {
  const imageBGUrl = data.image;
  const toUpload = data.toUpload;
  bgImageUrl.value = imageBGUrl;
  if (toUpload) {
    try {
      state.campaign_bg_image = await getFileFromURL(imageBGUrl, "image.jpg", "image/jpeg");
      state.campaign_bg_image_deleted = false;
    } catch (error) {
      console.error("Error converting URL to file:", error);
    }
  } else {
    //remove previous image if there was one
    state.campaign_bg_image = null;
    // if there is no image delete previous one
    if (!imageBGUrl) {
      state.campaign_bg_image_deleted = true;
    }
  }
}

// Pass the file uploaded event to the parent component
function handleBGUploaded(imgFile) {
  if (imgFile) {
    const imageBGUrl = URL.createObjectURL(imgFile);
    bgImageUrl.value = imageBGUrl;
    state.campaign_bg_image = imgFile;
    state.campaign_bg_image_deleted = false;
  } else {
    bgImageUrl.value = false;
    state.campaign_bg_image = null;
    state.campaign_bg_image_deleted = true;
  }
}

async function handleHeaderImage(data) {
  const imageId = data.image;
  const toUpload = data.toUpload;
  state.publicCampaignData.branding.banner_image_id = imageId;

  if (imageId) {
    if (toUpload) {
      const imageUrl = getImage(imageId);
      try {
        state.campaign_image = await getFileFromURL(imageUrl, "image.jpg", "image/jpeg");
      } catch (error) {
        console.error("Error converting URL to file:", error);
      }
    } else {
      //remove previous image if there was one
      state.campaign_image = null;
    }
    state.campaign_image_deleted = false;
  } else {
    // handle image deletion
    state.campaign_image_deleted = true;
    //remove previous image if there was one
    state.campaign_image = null;
  }
}

function prepareUploadHeaderImage(imgFile) {
  state.publicCampaignData.branding.banner_image_id = "tmp_new_id"; // Have a temp id so image can be restored
  state.campaign_image = imgFile;

  // Reset selection on changing header
  if (campaignThemeRef.value && campaignThemeRef.value.resetSelection) {
    campaignThemeRef.value.resetSelection(); // Call the method on the child component
  }
}

onMounted(() => {
  if (campaign.value?.branding?.background_image_id) {
    const imageBGUrl = getImage(campaign.value.branding.background_image_id);
    bgImageUrl.value = imageBGUrl;
  }
});
</script>
